import React, { useEffect, useState } from 'react';
import type { ActivityEvent } from '@pagepilot/domain';
import { Card, EmptyState } from '../ui';

export function ActivityScreen({ pageId }: { pageId?: string } = {}) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [filter, setFilter] = useState('');
  const [severity, setSeverity] = useState<'all' | 'info' | 'warn' | 'error'>('all');

  useEffect(() => {
    const load = async () => {
      try {
        setEvents((await window.pagepilot.engine.activity(300)) as ActivityEvent[]);
      } catch { /* engine offline */ }
    };
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, []);

  const visible = events.filter(
    (e) =>
      // Inside a Page workspace only show this Page's events plus engine-wide
      // (page-less) events; standalone shows everything.
      (!pageId || e.pageId === pageId || e.pageId === null) &&
      (severity === 'all' || e.severity === severity) &&
      (filter === '' || e.message.toLowerCase().includes(filter.toLowerCase()) || e.eventCode.toLowerCase().includes(filter.toLowerCase())),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Activity & Logs</h1>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search messages or event codes…"
          className="w-72 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-sm outline-none focus:border-brand-400"
        />
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as typeof severity)}
          className="rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm"
        >
          <option value="all">All severities</option>
          <option value="info">Info</option>
          <option value="warn">Warnings</option>
          <option value="error">Errors</option>
        </select>
      </div>

      <Card>
        {visible.length === 0 ? (
          <EmptyState title="No log entries" hint="Sanitized engine events appear here. Secrets are never logged." />
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="text-neutral-500">
              <tr>
                <th className="pb-2 pr-3 font-medium">Time</th>
                <th className="pb-2 pr-3 font-medium">Code</th>
                <th className="pb-2 font-medium">Message</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <tr key={e.id} className="border-t border-neutral-800/60 align-top">
                  <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums text-neutral-500">
                    {new Date(e.at).toLocaleString()}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-neutral-500">{e.eventCode}</td>
                  <td className={`py-1.5 ${e.severity === 'error' ? 'text-red-400' : e.severity === 'warn' ? 'text-amber-400' : 'text-neutral-300'}`}>
                    {e.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
